"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Bath,
  BedDouble,
  Bell,
  Clock,
  Loader2,
  MapPin,
  Maximize2,
  Mic,
  MicOff,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type AIProperty = {
  id: string;
  title: string;
  slug: string;
  propertyType: string;
  region: string;
  city?: string;
  area: number;
  bedrooms?: number;
  bathrooms?: number;
  price: number;
  rent_type?: string;
  is_sale: boolean;
  thumbnail: string;
  url: string;
};
type Filters = {
  propertyType: string[];
  region: string[];
  city: string | null;
  purpose: "buy" | "rent";
  rentType: string | null;
  minPrice: number | null;
  maxPrice: number | null;
  minArea: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  keywords: string[];
};
type SearchResult = { reply: string; level: number; filters: Filters; properties: AIProperty[]; link: string };

const TYPE_AR: Record<string, string> = {
  apartment: "شقة", villa: "فيلا", chalet: "شاليه", commercial: "تجاري", land: "أرض", office: "مكتب",
  shop: "محل", warehouse: "مستودع", house: "منزل", studio: "استوديو", duplex: "دوبلكس", penthouse: "بنتهاوس",
  farm: "مزرعة", camp: "مخيم", floor: "دور", building: "عمارة", room: "غرفة", residential_land: "أرض سكنية",
  commercial_land: "أرض تجارية", showroom: "معرض", hotel_apartment: "شقة فندقية", rest_house: "استراحة",
};
const REGION_AR: Record<string, string> = {
  riyadh: "الرياض", makkah: "مكة المكرمة", madinah: "المدينة المنورة", qassim: "القصيم",
  eastern_province: "المنطقة الشرقية", asir: "عسير", tabuk: "تبوك", hail: "حائل",
  northern_borders: "الحدود الشمالية", jazan: "جازان", najran: "نجران", al_bahah: "الباحة", al_jouf: "الجوف",
};

const EXAMPLES_AR = [
  "أبغى فيلا في الرياض تحت 2 مليون",
  "شقة 3 غرف للإيجار في جدة",
  "استراحة في الدمام فيها مسبح",
  "أرض تجارية في القصيم",
  "دوبلكس جديد في مكة بين مليون ومليون ونص",
  "شقة مفروشة إيجار يومي في الخبر",
  "بيت قريب من المدارس في الرياض بـ 900 ألف",
  "بنتهاوس مطل في جدة",
];
const EXAMPLES_EN = [
  "Villa in Riyadh under 2M",
  "3-bedroom apartment for rent in Jeddah",
  "Rest house with a pool in Dammam",
  "Commercial land in Qassim",
  "New duplex in Makkah between 1M and 1.5M",
  "Furnished apartment, daily rent, Khobar",
];

const RECENT_KEY = "sna-ai-recent";
const pick = (arr: string[], n: number) => [...arr].sort(() => Math.random() - 0.5).slice(0, n);

/* render **bold** segments */
function renderReply(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith("**") && seg.endsWith("**") ? (
      <strong key={i} className="font-extrabold text-amber-300">{seg.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{seg}</span>
    ),
  );
}

const fmtMoney = (n: number, ar: boolean) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)} ${ar ? "مليون" : "M"}` : n >= 1000 ? `${Math.round(n / 1000)} ${ar ? "ألف" : "K"}` : String(n);

export default function PropertyAISearch() {
  const locale = useLocale();
  const isRtl = locale === "ar";
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState("");
  const [examples, setExamples] = useState<string[]>([]);
  const [recent, setRecent] = useState<string[]>([]);
  const [listening, setListening] = useState(false);
  const recRef = useRef<any>(null);

  const speechSupported = typeof window !== "undefined" && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  /* open: lock scroll, focus, fresh examples + recent */
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setExamples(pick(isRtl ? EXAMPLES_AR : EXAMPLES_EN, 4));
      try {
        setRecent(JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"));
      } catch {
        setRecent([]);
      }
      setTimeout(() => inputRef.current?.focus(), 200);
    } else {
      document.body.style.overflow = "";
      recRef.current?.stop?.();
      setListening(false);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, isRtl]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const remember = (q: string) => {
    try {
      const next = [q, ...recent.filter((r) => r !== q)].slice(0, 5);
      setRecent(next);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {}
  };

  const run = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || loading) return;
      if (inputRef.current) inputRef.current.value = message;
      setLoading(true);
      setError("");
      try {
        const apiBase = process.env.NEXT_PUBLIC_API_URI ?? "http://localhost:8000";
        const res = await fetch(`${apiBase}/api/v1/ai/property-search`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-api-key": process.env.NEXT_PUBLIC_API_KEY || "" },
          body: JSON.stringify({ message, locale, siteUrl: window.location.origin }),
        });
        const data = await res.json();
        if (!res.ok || data?.error) {
          setError(typeof data?.error === "string" ? data.error : data?.message || "Search failed");
          return;
        }
        setResult(data);
        remember(message);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, locale, recent],
  );

  /* voice input (Web Speech API — free, Arabic supported) */
  const toggleVoice = () => {
    if (!speechSupported) return;
    if (listening) {
      recRef.current?.stop?.();
      setListening(false);
      return;
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = isRtl ? "ar-SA" : "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e: any) => {
      const text = e.results?.[0]?.[0]?.transcript || "";
      setListening(false);
      if (text) run(text);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  /* removable filter chips — rebuild a query without that filter */
  const chips = useMemo(() => {
    const f = result?.filters;
    if (!f) return [];
    const out: { k: string; label: string; strip: (q: string) => string }[] = [];
    const ar = isRtl;
    out.push({ k: "purpose", label: f.purpose === "rent" ? (ar ? "للإيجار" : "Rent") : ar ? "للبيع" : "Buy", strip: (q) => q });
    f.propertyType.forEach((t) => out.push({ k: `t-${t}`, label: ar ? TYPE_AR[t] || t : t, strip: (q) => q.replace(new RegExp(TYPE_AR[t] || t, "gi"), "") }));
    if (f.city) out.push({ k: "city", label: f.city, strip: (q) => q.replace(new RegExp(f.city as string, "gi"), "") });
    else f.region.forEach((r) => out.push({ k: `r-${r}`, label: ar ? REGION_AR[r] || r : r, strip: (q) => q.replace(new RegExp(REGION_AR[r] || r, "gi"), "") }));
    if (f.maxPrice) out.push({ k: "max", label: `${ar ? "حتى" : "up to"} ${fmtMoney(f.maxPrice, ar)}`, strip: (q) => q.replace(/(تحت|أقل من|اقل من|under|below|max)?\s*[\d.,٠-٩]+\s*(مليون|ألف|الف|m|k|million|thousand)?(\s*(و|and)\s*(نص|نصف|ربع))?/gi, "") });
    if (f.minPrice && !f.maxPrice) out.push({ k: "min", label: `${ar ? "من" : "from"} ${fmtMoney(f.minPrice, ar)}`, strip: (q) => q.replace(/(فوق|أكثر من|اكثر من|above|over|min)?\s*[\d.,٠-٩]+\s*(مليون|ألف|الف|m|k)?/gi, "") });
    if (f.bedrooms) out.push({ k: "bed", label: `${f.bedrooms} ${ar ? "غرف" : "beds"}`, strip: (q) => q.replace(/[\d٠-٩]+\s*(غرف|غرفة|غرفه|bed(room)?s?)/gi, "") });
    if (f.bathrooms) out.push({ k: "bath", label: `${f.bathrooms} ${ar ? "حمام" : "baths"}`, strip: (q) => q.replace(/[\d٠-٩]+\s*(حمام|حمامات|bath(room)?s?)/gi, "") });
    f.keywords.forEach((kw) => out.push({ k: `kw-${kw}`, label: kw, strip: (q) => q.replace(new RegExp(kw, "gi"), "") }));
    return out;
  }, [result, isRtl]);

  const fmtPrice = (n: number) => new Intl.NumberFormat("en-US").format(n || 0);
  const lastQuery = inputRef.current?.value || "";

  return (
    <>
      {/* ── Floating button ──────────────────────────────────── */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setOpen(true)}
            dir={isRtl ? "rtl" : "ltr"}
            className={`fixed bottom-6 z-[88] flex items-center gap-2.5 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 px-5 py-4 text-[#0d3589] shadow-[0_12px_30px_-6px_rgba(245,158,11,0.6)] transition-shadow hover:shadow-[0_16px_40px_-6px_rgba(245,158,11,0.7)] ${isRtl ? "right-6" : "left-6"}`}
            aria-label={isRtl ? "البحث الذكي عن العقارات" : "Smart property search"}
          >
            <span className="absolute inset-0 rounded-full bg-amber-300 opacity-40 animate-ping" />
            <Sparkles size={22} className="relative" />
            <span className="relative hidden text-sm font-extrabold md:inline">{isRtl ? "بحث ذكي" : "Smart Search"}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Modal ────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            dir={isRtl ? "rtl" : "ltr"}
            className="fixed inset-0 z-[95] flex items-start justify-center overflow-y-auto bg-slate-950/60 p-4 backdrop-blur-sm sm:p-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="relative my-auto w-full max-w-3xl overflow-hidden rounded-[28px] bg-white shadow-2xl"
            >
              {/* header */}
              <div className="relative overflow-hidden bg-gradient-to-br from-[#0a2a6e] via-[#0d3589] to-[#071f52] p-6 sm:p-7">
                <div className="pointer-events-none absolute -top-16 -right-10 size-56 rounded-full bg-amber-400/15 blur-3xl" />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/70 to-transparent" />
                <button onClick={() => setOpen(false)} className={`absolute top-5 grid size-9 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 ${isRtl ? "left-5" : "right-5"}`} aria-label="Close">
                  <X size={18} />
                </button>

                <div className="relative mb-5 flex items-center gap-3">
                  <div className="grid size-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 shadow-lg shadow-amber-500/30">
                    <Sparkles size={22} className="text-[#0d3589]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black tracking-tight text-white sm:text-xl">{isRtl ? "البحث الذكي عن العقارات" : "Smart Property Search"}</h3>
                    <p className="mt-0.5 text-sm text-blue-100/70">{isRtl ? "اكتب أو تكلّم بلهجتك… ونفهمك ونجيب لك الأقرب" : "Type or speak naturally — we'll find the closest match"}</p>
                  </div>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    run(inputRef.current?.value ?? "");
                  }}
                  className="relative flex flex-col items-stretch gap-3 sm:flex-row"
                >
                  <div className="group relative flex-1">
                    <Search size={19} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-[#0d3589] ${isRtl ? "right-4" : "left-4"}`} />
                    <input
                      ref={inputRef}
                      placeholder={listening ? (isRtl ? "🎙️ أسمعك… تكلّم الآن" : "🎙️ Listening…") : isRtl ? "مثال: أبغى شقة 3 غرف في جدة تحت 800 ألف" : "e.g. 3-bed apartment in Jeddah under 800K"}
                      disabled={loading}
                      className={`h-14 w-full rounded-2xl bg-white/95 text-[15px] font-semibold text-slate-800 shadow-lg outline-none ring-2 ring-transparent transition-all placeholder:font-medium placeholder:text-slate-400 focus:ring-amber-300/60 disabled:opacity-60 ${isRtl ? "pr-12 pl-14" : "pl-12 pr-14"}`}
                    />
                    {speechSupported && (
                      <button
                        type="button"
                        onClick={toggleVoice}
                        title={isRtl ? "بحث صوتي" : "Voice search"}
                        className={`absolute top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-xl transition-all ${isRtl ? "left-2" : "right-2"} ${listening ? "bg-rose-500 text-white animate-pulse" : "bg-[#0d3589]/8 text-[#0d3589] hover:bg-[#0d3589] hover:text-white"}`}
                      >
                        {listening ? <MicOff size={17} /> : <Mic size={17} />}
                      </button>
                    )}
                  </div>
                  <button type="submit" disabled={loading} className="flex h-14 shrink-0 items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 px-7 text-[15px] font-extrabold text-[#0d3589] shadow-lg shadow-amber-500/30 transition-all hover:brightness-105 active:scale-[0.97] disabled:opacity-60">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                    {isRtl ? "ابحث" : "Search"}
                  </button>
                </form>

                {/* examples / recent */}
                {!result && !loading && (
                  <div className="relative mt-4 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-blue-100/50">{isRtl ? "جرّب:" : "Try:"}</span>
                      {examples.map((ex) => (
                        <button key={ex} onClick={() => run(ex)} className="rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-medium text-blue-50 ring-1 ring-white/15 transition-all hover:bg-white/20 hover:ring-amber-300/40">{ex}</button>
                      ))}
                    </div>
                    {recent.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex items-center gap-1 text-xs font-medium text-blue-100/50"><Clock size={11} /> {isRtl ? "الأخيرة:" : "Recent:"}</span>
                        {recent.map((r) => (
                          <button key={r} onClick={() => run(r)} className="rounded-full bg-amber-300/10 px-3 py-1 text-xs font-medium text-amber-100 ring-1 ring-amber-300/20 hover:bg-amber-300/20">{r}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* reply + filter chips */}
                {result && !loading && (
                  <div className="relative mt-4 space-y-3">
                    <p className="text-[14px] leading-relaxed text-blue-50/90">{renderReply(result.reply)}</p>
                    {chips.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] font-bold text-blue-100/50">{isRtl ? "فهمت:" : "Understood:"}</span>
                        {chips.map((c) => (
                          <span key={c.k} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-bold text-white ring-1 ring-white/15">
                            {c.label}
                            {c.k !== "purpose" && (
                              <button
                                type="button"
                                onClick={() => {
                                  const q = c.strip(lastQuery).replace(/\s{2,}/g, " ").trim();
                                  if (q) run(q);
                                }}
                                className="grid size-4 place-items-center rounded-full bg-white/10 hover:bg-rose-500"
                                aria-label="remove"
                              >
                                <X size={10} />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* body */}
              <div className="max-h-[55vh] overflow-y-auto bg-slate-50/50 p-5 sm:p-6">
                {error && <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {error}</div>}

                {loading && (
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="overflow-hidden rounded-2xl border border-slate-100 bg-white">
                        <div className="aspect-[4/3] animate-pulse bg-slate-100" />
                        <div className="space-y-2 p-3">
                          <div className="h-3.5 w-3/4 animate-pulse rounded bg-slate-100" />
                          <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!loading && !result && !error && (
                  <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400">
                    <div className="mb-4 grid size-16 place-items-center rounded-3xl bg-[#0d3589]/5"><Search size={28} className="text-[#0d3589]/40" /></div>
                    <p className="text-sm font-medium">{isRtl ? "اكتب طلبك بالأعلى — أو اضغط المايك وتكلّم" : "Type your request above — or tap the mic and speak"}</p>
                  </div>
                )}

                {!loading && result && (
                  <>
                    {result.level >= 1 && result.properties.length > 0 && (
                      <div className="mb-4 flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs font-bold text-amber-800">
                        <Sparkles size={14} /> {isRtl ? "وسّعنا البحث تلقائياً لنعرض لك أقرب البدائل" : "We widened the search automatically to show the closest alternatives"}
                      </div>
                    )}

                    {result.properties.length > 0 ? (
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                        {result.properties.map((p, idx) => (
                          <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
                            <Link href={`/${locale}/properties/${p.slug}`} onClick={() => setOpen(false)} className="group relative block overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80 transition-all duration-300 hover:shadow-[0_18px_40px_-12px_rgba(13,53,137,0.28)] hover:ring-2 hover:ring-amber-300">
                              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                                <Image src={p.thumbnail} alt={p.title} fill sizes="320px" className="object-cover transition-transform duration-500 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                <span className="absolute start-2.5 top-2.5 rounded-full bg-amber-300 px-2 py-0.5 text-[10px] font-bold text-[#0d3589] shadow">
                                  {p.is_sale ? (isRtl ? "للبيع" : "Sale") : isRtl ? "للإيجار" : "Rent"}
                                </span>
                                {idx === 0 && result.level === 0 && (
                                  <span className="absolute end-2.5 top-2.5 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white shadow">{isRtl ? "الأفضل لك" : "Best match"}</span>
                                )}
                                <p className="absolute bottom-2.5 start-2.5 text-base font-black text-white drop-shadow-lg">
                                  {fmtPrice(p.price)}
                                  <span className="ms-1 text-[10px] font-semibold text-amber-200">{isRtl ? "ريال" : "SAR"}{!p.is_sale && (p.rent_type === "daily" ? (isRtl ? "/يوم" : "/day") : isRtl ? "/سنة" : "/yr")}</span>
                                </p>
                              </div>
                              <div className="p-3">
                                <h4 className="line-clamp-1 text-sm font-bold text-slate-900 transition-colors group-hover:text-[#0d3589]">{p.title}</h4>
                                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                                  <span className="flex items-center gap-1"><MapPin size={12} className="text-amber-500" />{p.city || (isRtl ? REGION_AR[p.region] ?? p.region : p.region)}</span>
                                  {p.area ? <span className="flex items-center gap-1"><Maximize2 size={11} className="text-amber-500" />{p.area} {isRtl ? "م²" : "m²"}</span> : null}
                                  {p.bedrooms ? <span className="flex items-center gap-1"><BedDouble size={12} className="text-amber-500" />{p.bedrooms}</span> : null}
                                  {p.bathrooms ? <span className="flex items-center gap-1"><Bath size={12} className="text-amber-500" />{p.bathrooms}</span> : null}
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <p className="py-8 text-center text-sm text-slate-400">{isRtl ? "لا توجد نتائج مطابقة — جرّب توسيع البحث." : "No matches — try widening your search."}</p>
                    )}

                    <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                      <Link href={result.link.replace(window.location.origin, "")} onClick={() => setOpen(false)} className="group inline-flex items-center gap-2 rounded-full bg-[#0d3589] px-7 py-3 text-sm font-bold text-white shadow-lg shadow-[#0d3589]/25 transition-all hover:bg-[#0a2a6e]">
                        {isRtl ? "عرض كل النتائج المطابقة" : "View all matching results"}
                        <ArrowLeft size={16} className={isRtl ? "" : "rotate-180"} />
                      </Link>
                      <Link href={result.link.replace(window.location.origin, "")} onClick={() => setOpen(false)} className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-5 py-3 text-sm font-bold text-amber-800 transition-all hover:bg-amber-100">
                        <Bell size={15} /> {isRtl ? "احفظ البحث ونبّهني بالجديد" : "Save search & alert me"}
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
